import { useState } from "react";
import { Banner, Flex, QueryLoader, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { type MinimalQueryResult } from "@jsq/ds/types";
import { ExtendedInfoTooltip } from "~/pages/Compliance/Common/ExtendedInfoTooltip";
import { ExistingPositionsModal } from "~/pages/Compliance/ExistingPositionsModal";
import { CellValue } from "../CompareTable/CellValue";
import * as styles from "./styles_vanilla.css";
import { hasProfileDiscrepancy } from "./utils";
import type { MatchCell, MatchStatus, ProfileMatchData } from "./types";
import type { PositionRowData } from "~/pages/Compliance/ExistingPositionsModal/types";

export type ProfileToInvestorViewProps = {
  matchData: ProfileMatchData | null;
  queryResult: MinimalQueryResult;
  /** Shades the compliance profile column to mark it as the active selection. */
  highlightProfileColumn?: boolean;
  /**
   * Called when the user clicks "Select different profile" on the discrepancy banner. Passing
   * this also swaps in review-specific banner copy, not just the button.
   */
  onEditProfile?: () => void;
};

type ComparisonTableProps = {
  matchData: ProfileMatchData;
  hasInvestmentAccount: boolean;
  highlightProfileColumn: boolean;
  onPositionsClick: (target: PositionsTarget) => void;
};

type PositionsTarget =
  | { kind: "profile"; profileId: number }
  | { kind: "account"; accountName: string; rows: PositionRowData[]; viewAllHref: string };

const positionsTargetFor = (cell: MatchCell): PositionsTarget | null => {
  if (cell.profileId != null) return { kind: "profile", profileId: cell.profileId };
  if (cell.accountPositions) return { kind: "account", ...cell.accountPositions };
  return null;
};

const PositionsCell = ({
  cell,
  onPositionsClick,
}: {
  cell: MatchCell;
  onPositionsClick: (target: PositionsTarget) => void;
}) => {
  const target = positionsTargetFor(cell);
  return (
    <CellValue
      cell={cell}
      isLink={cell.isLink}
      onLinkClick={target ? () => onPositionsClick(target) : undefined}
    />
  );
};

const StatusCell = ({ status }: { status: MatchStatus }) => {
  if (status === "na") {
    return <Text variant="secondary-md">N/A</Text>;
  }
  const isMatch = status === "match";
  return (
    <Flex alignItems="center" gap={Sizes.SM}>
      <Icon
        type={isMatch ? "check" : "exclamation-triangle"}
        variant="outline"
        color={isMatch ? Colors.Success600 : Colors.Warning600}
      />
      <Text variant="regular-md" className={isMatch ? styles.matchText : styles.mismatchText}>
        {isMatch ? "Matches" : "Mismatch detected"}
      </Text>
    </Flex>
  );
};

const ComparisonTable = ({
  matchData,
  hasInvestmentAccount,
  highlightProfileColumn,
  onPositionsClick,
}: ComparisonTableProps) => (
  <div className={styles.table}>
    <Flex className={styles.headerRow}>
      <div className={styles.labelCell} />
      <Flex flexDirection="column" alignItems="flex-start" className={styles.headerCell}>
        <Text variant="secondary-md">Compliance profile</Text>
        {matchData.complianceProfileSubtitle !== null && (
          <Text variant="regular-md" className={styles.currentSelection}>
            {matchData.complianceProfileSubtitle}
          </Text>
        )}
      </Flex>
      {hasInvestmentAccount && (
        <Flex flexDirection="column" alignItems="flex-start" className={styles.headerCell}>
          <Text variant="secondary-md">Investment account</Text>
          <Text variant="regular-md">{matchData.investmentAccountSubtitle}</Text>
        </Flex>
      )}
      {hasInvestmentAccount && (
        <Flex flexDirection="column" alignItems="flex-start" className={styles.headerCell}>
          <Flex alignItems="center" gap={Sizes.XS}>
            <Text variant="secondary-md">Profile validation</Text>
            <ExtendedInfoTooltip message="We verify that your selected compliance profile matches the investment account information provided for the position associated with the subscription linked to this case.">
              <Icon type="info-circle" variant="outline" color={Colors.Neutral600} />
            </ExtendedInfoTooltip>
          </Flex>
          {matchData.matchPercent !== null && (
            <Text variant="regular-md">{Math.round(matchData.matchPercent)}% Match</Text>
          )}
        </Flex>
      )}
    </Flex>
    {matchData.rows.map((rowItem) => (
      <Flex key={rowItem.label} className={styles.row}>
        <Text variant="secondary-md" className={styles.labelCell}>
          {rowItem.label}
        </Text>
        <div
          className={
            highlightProfileColumn ? `${styles.cell} ${styles.complianceCell}` : styles.cell
          }
        >
          <PositionsCell cell={rowItem.complianceProfile} onPositionsClick={onPositionsClick} />
        </div>
        {hasInvestmentAccount && (
          <div className={styles.cell}>
            <PositionsCell
              cell={rowItem.investmentAccount}
              onPositionsClick={onPositionsClick}
            />
          </div>
        )}
        {hasInvestmentAccount && (
          <div className={styles.cell}>
            <StatusCell status={rowItem.status} />
          </div>
        )}
      </Flex>
    ))}
  </div>
);

export const ProfileToInvestorView = ({
  matchData,
  queryResult,
  highlightProfileColumn = true,
  onEditProfile,
}: ProfileToInvestorViewProps) => {
  const [target, setTarget] = useState<PositionsTarget | null>(null);

  const hasDiscrepancy = hasProfileDiscrepancy(matchData);
  const discrepancyDescription = onEditProfile ? (
    <>
      The selected compliance profile details do not match the investment account information of
      the subscription URL position.
      <br />
      You can still continue to create this case or select a different profile.
    </>
  ) : (
    "The selected compliance profile details do not match the investment account information. You can still continue or select a different profile."
  );

  return (
    <QueryLoader result={queryResult}>
      {matchData ? (
        <Flex flexDirection="column" gap={Sizes.MD} className={styles.viewRoot}>
          {hasDiscrepancy && (
            <Banner
              variant="warning"
              title="Review the profile validation discrepancy"
              isDescriptionJsx
              description={
                <Text variant="secondary-md" className={styles.bannerDescription}>
                  {discrepancyDescription}
                </Text>
              }
              buttonProps={
                onEditProfile
                  ? { children: "Select different profile", onClick: onEditProfile }
                  : undefined
              }
            />
          )}
          <ComparisonTable
            matchData={matchData}
            hasInvestmentAccount={matchData.showInvestmentAccount}
            highlightProfileColumn={highlightProfileColumn}
            onPositionsClick={setTarget}
          />
          {target?.kind === "profile" && (
            <ExistingPositionsModal
              profileId={target.profileId}
              onClose={() => setTarget(null)}
            />
          )}
          {target?.kind === "account" && (
            <ExistingPositionsModal
              accountName={target.accountName}
              rows={target.rows}
              viewAllHref={target.viewAllHref}
              onClose={() => setTarget(null)}
            />
          )}
        </Flex>
      ) : (
        <Text variant="regular-md">No comparison is available for this profile.</Text>
      )}
    </QueryLoader>
  );
};
